import { Component, Input, OnInit } from '@angular/core';
import {
  Confirmation,
  ConfirmationService,
  LazyLoadEvent,
  MessageService,
  SelectItem,
} from 'primeng';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ICustomer } from 'src/app/interfaces/customer';
import { IPageResult } from 'src/app/interfaces/page-result';
import {
  ICustomerServiceFee,
  ICustomerServiceFeeFilter,
  IServiceFeeType,
} from 'src/app/interfaces/service-fee';
import { CustomerService, OrganisationsService } from 'src/app/services';
import { CustomerServiceFee } from 'src/app/services/customer-service-fee.service';
import { ServiceFeeTypeService } from 'src/app/services/service-fee-type-service';

@Component({
  selector: 'app-customer-service-fee',
  templateUrl: './customer-service-fee.component.html',
  styleUrls: ['./customer-service-fee.component.css'],
})
export class CustomerServiceFeeComponent implements OnInit {
  @Input() isReadonly: boolean;

  public serviceFeeTypes: IServiceFeeType[];
  public customerServiceFees: IPageResult<ICustomerServiceFee>;
  public filter: Subject<ICustomerServiceFeeFilter>;
  public filterVal: ICustomerServiceFeeFilter;
  public cols = [];
  public customerServiceFee: ICustomerServiceFee;
  public newCustomerServiceFee: boolean = false;
  public displayDialog: boolean = false;
  public customerDepartments = [];
  public deptartmentOptions: SelectItem[];
  public teamOptions: SelectItem[];
  public serviceFeeTypeOptions: SelectItem[];
  public orgTypeId: number;
  constructor(
    private customerServiceFeeService: CustomerServiceFee,
    private serviceFeeTypeService: ServiceFeeTypeService,
    private customerService: CustomerService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private organisationService: OrganisationsService,
  ) {}
  public _customerId: number;
  public _customerName: string;
  @Input('customerId')
  public set customerId(value: number) {
    this._customerId = value;
  }
  @Input('customerName')
  public set customerName(value: string) {
    this._customerName = value;
  }

  ngOnInit(): void {
    this.organisationService
      .getCurrentUserOrgTypeId()
      .then((res) => {
        this.orgTypeId = res;
      })
      .catch((err) => {
        console.log('Error fetching current user org type');
      });
    this.initDefaultFilter();
    this.filter = new Subject<ICustomerServiceFeeFilter>();
    this.filter.pipe(debounceTime(500)).subscribe((f) => {
      this.loadCustomerServiceFees(f);
      this.filterVal = f;
    });
    this.cols = [
      { field: 'name', header: 'Name', sortField: 'name' },
      { field: 'serviceFeeDisplayName', header: 'Service Fee Type', sortField: 'serviceFeeTypeId' },
      { field: 'feeValue', header: 'Fee Value', sortField: 'feeValue' },
      { field: 'departmentName', header: 'Department', sortField: 'departmentId' },
      { field: 'teamName', header: 'Team', sortField: 'teamId' },
      { field: 'isTaskLevel', header: 'Is Task Level', sortField: 'isTaskLevel' },
      { field: 'isMandatory', header: 'Is Mandatory', sortField: 'isMandatory' },
    ];
    if (!this._customerId) {
      this.cols.unshift({ field: 'customerName', header: 'Customer', sortField: 'customerId' });
    }
    this.loadServiceFeeTypes();
    if (this._customerId) {
      this.getCustomerDepartments(this._customerId);
    }
  }

  initDefaultFilter() {
    this.filterVal = {
      first: 0,
      rows: 10,
      sortField: 'Id', // ASC
      sortOrder: 1,
      filters: {
        customerId: { value: this._customerId, matchMode: 'equals' },
      },
    };
  }

  onGridLazyLoad(event: LazyLoadEvent) {
    this.filterVal.first = event.first;
    this.filterVal.rows = event.rows;
    this.filterVal.sortField = event.sortField;
    this.filterVal.sortOrder = event.sortOrder;

    this.filter.next(this.filterVal);
  }

  loadServiceFeeTypes() {
    this.serviceFeeTypeService
      .getAll()
      .then((res) => {
        this.serviceFeeTypes = res;
        this.serviceFeeTypeOptions = res.map((serviceFeeType) => {
          return { label: serviceFeeType.displayName, value: serviceFeeType.id };
        });
        this.serviceFeeTypeOptions.unshift({ label: 'Select Service Fee', value: null });
      })
      .catch((err) => {
        console.log('An error occurred loading Service Fee Types');
      })
      .finally();
  }

  loadCustomerServiceFees(filter: ICustomerServiceFeeFilter) {
    this.customerServiceFeeService
      .getPage(filter)
      .then((res) => {
        this.customerServiceFees = res;
      })
      .catch((err) => {
        console.log('An error occurred loading Customer Service Fees');
      })
      .finally(() => {});
  }

  showDialogToAdd() {
    this.newCustomerServiceFee = true;
    this.customerServiceFee = <ICustomerServiceFee>{ customerId: this._customerId };
    this.displayDialog = true;
  }

  onRowSelect(event) {
    if (this.isReadonly) {
      return;
    }

    this.newCustomerServiceFee = false;
    this.customerServiceFee = this.cloneCustomerServiceFee(event.data);
    if (event.data.departmentId) {
      this.handleCustomerDepartmentChange(event.data.departmentId);
    }
    this.displayDialog = true;
  }

  cloneCustomerServiceFee(c: ICustomerServiceFee): ICustomerServiceFee {
    let customerServiceFee = <ICustomerServiceFee>{};
    for (let prop in c) {
      customerServiceFee[prop] = c[prop];
    }
    return customerServiceFee;
  }

  save() {
    if (!this.customerServiceFee.customerId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Customer is not specified',
      });
      return;
    }
    if (!this.customerServiceFee.serviceFeeTypeId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Service fee type is required',
      });
    }
    if (!this.customerServiceFee.feeValue) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Service fee value is required',
      });
    }
    if (this.customerServiceFee.id) {
      this.customerServiceFeeService
        .update(this.customerServiceFee.id, this.customerServiceFee)
        .then((res) => {
          this.customerServiceFee = null;
          this.displayDialog = false;
          this.filter.next(this.filterVal);
        })
        .catch((err) => {
          console.log('An error occurred updating Customer Service Fee');
        })
        .finally();
    } else {
      this.customerServiceFeeService
        .create(this.customerServiceFee)
        .then((res) => {
          this.customerServiceFee = null;
          this.displayDialog = false;
          this.filter.next(this.filterVal);
        })
        .catch((err) => {
          console.log('An error occurred creating Customer Service Fee');
        })
        .finally();
    }
  }

  delete() {
    this.confirmationService.confirm({
      message:
        'Are you sure that you want to delete this service type? You will need to ensure existing associations are removed first',
      accept: () => {
        this.serviceFeeTypeService
          .delete(this.customerServiceFee.id)
          .then((res) => {
            this.customerServiceFee = null;
            this.displayDialog = false;
            this.filter.next(this.filterVal);
          })
          .catch((err) => {
            console.log('An error occurred deleting Customer Service Fee');
          })
          .finally();
      },
      reject: () => {
        this.customerServiceFee = null;
        this.displayDialog = false;
      },
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      icon: 'pi pi-exclamation-triangle',
    });
  }

  getCustomerDepartments(customerId: number) {
    this.customerService.getDepartments(customerId).then((depts) => {
      this.customerDepartments = depts;
      this.deptartmentOptions = depts.map((dept) => {
        return { label: dept.name, value: dept.id };
      });

      // insert 'All' as a department option
      this.deptartmentOptions.unshift({ label: 'All Departments', value: null });
    });
  }

  handleCustomerDepartmentChange(departmentId) {
    this.teamOptions = [];
    var selectedDepartment = this.customerDepartments.filter((val, i) => val.id == departmentId);
    if (selectedDepartment) {
      this.teamOptions = selectedDepartment[0].teams.map((team) => {
        return { label: team.name, value: team.id };
      });
      this.teamOptions.unshift({ label: 'All Teams', value: null });
    }
  }

  handleCustomerTeamChange(teamId) {
    this.filter.next(this.filterVal);
  }
  onFormOrganisationChange(orgId) {
    this.customerServiceFee.customerId = orgId;
    this.getCustomerDepartments(orgId);
  }

  onTableOrganisationChange(selectedOrgId) {
    this.filterVal.filters.customerId.value = selectedOrgId;
    this.filter.next(this.filterVal);
    this.getCustomerDepartments(selectedOrgId);
  }
}
